import { Outlet, useLocation } from 'react-router-dom';
import { styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import ClipboardIcon from 'src/assets/icons/clipboard-icon.png';
import { HEADER_HEIGHT, MOBILE_MAX_WIDTH } from 'src/assets/static/constants';
import { LoadingScreen } from 'src/components/loading-screen';
import ScrollToTop from 'src/components/scroll-to-top';

import { DashboardHeader } from '../header';

interface DashboardLayoutProps {
  needClipboard?: boolean;
}

function DashboardLayout({ needClipboard = true }: DashboardLayoutProps) {
  const location = useLocation();

  return (
    <Container>
      <DashboardHeader />
      <MainContainer>
        {needClipboard ? (
          <ChartContainer
            sx={{
              // 휴대폰 · 태블릿에서는 카드가 세로로 쌓여 길어지므로 화면 높이 대신 내용 높이를 따른다
              minHeight: {
                xs: 'auto',
                xl: location.pathname.includes('/patient-management/patient-chart')
                  ? 'auto'
                  : 'calc(100% - 180px)',
              },
            }}
          >
            <Clipboard src={ClipboardIcon} alt="clipboard-icon" />
            <Outlet />
          </ChartContainer>
        ) : (
          <BasicContainer>
            <Outlet />
          </BasicContainer>
        )}
      </MainContainer>
      <ScrollToTop />
      <LoadingScreen />
    </Container>
  );
}

const Container = styled('div')({
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const MainContainer = styled('main')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  overflow: 'auto',
  padding: `0 40px 0 40px`,
  marginTop: HEADER_HEIGHT,
  backgroundColor: theme.palette.grey[100],
  [theme.breakpoints.down('md')]: {
    padding: '0 12px',
  },
}));

const ChartContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '20px',
  padding: '80px 60px 60px',
  margin: '90px 0px',
  backgroundColor: theme.palette.common.white,
  boxShadow: '0px 2px 35.6px 0px #00000017',
  height: 'auto',
  minHeight: 'calc(100% - 180px)',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    padding: '40px 16px 24px',
    margin: '40px 0px',
  },
}));

const Clipboard = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: '-25px',
  left: '50%',
  transform: 'translateX(-50%)',
  [theme.breakpoints.down('md')]: {
    width: '60%',
    maxWidth: '240px',
    top: '-16px',
  },
}));

const BasicContainer = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  maxWidth: MOBILE_MAX_WIDTH,
  borderRadius: '20px',
  margin: '40px auto',
  backgroundColor: theme.palette.common.white,
  boxShadow: '0px 2px 35.6px 0px #00000017',
}));

export default observer(DashboardLayout);
